import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Eye, Lock, Unlock, Plus, Trash2, Tag, Building2, Users, Briefcase, Package, Globe, Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Twitter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Layout } from '@/components/wiki/Layout';
import { TipTapEditor } from '@/components/wiki/TipTapEditor';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { id: 'Businesses', label: 'Businesses', icon: Building2 },
  { id: 'People', label: 'People', icon: Users },
  { id: 'Investors', label: 'Investors', icon: Briefcase },
  { id: 'Products', label: 'Products', icon: Package },
];

export default function EditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader, isPremium } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(null);
  const [newFieldName, setNewFieldName] = useState('');
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  
  const [content, setContent] = useState('');
  const [summary, setSummary] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [infobox, setInfobox] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [socialLinks, setSocialLinks] = useState({});
  const [customSocialLinks, setCustomSocialLinks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactInfo, setContactInfo] = useState({});
  const [isLocked, setIsLocked] = useState(false);
  const [showPremiumAlert, setShowPremiumAlert] = useState(false);
  const [newSocialDialogOpen, setNewSocialDialogOpen] = useState(false);
  const [newSocialName, setNewSocialName] = useState('');
  const [newSocialUrl, setNewSocialUrl] = useState('');

  // Check if user can edit premium features (is sponsor of this page)
  const canEditPremiumFeatures = page?.sponsor_id === user?.id || (isPremium() && page?.is_sponsored);

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  useEffect(() => {
    if (!user) {
      toast.error('Please log in to edit pages');
      navigate('/login');
      return;
    }

    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API}/pages/${slug}`);
        const pageData = response.data;
        setPage(pageData);
        setContent(pageData.content || '');
        setSummary(pageData.summary || '');
        setInfobox(pageData.infobox || {});
        setSelectedCategories(pageData.categories || []);
        setSocialLinks(pageData.social_links || {});
        setCustomSocialLinks(pageData.social_links?.custom || []);
        // Handle both old format (object) and new format (array) for contacts
        if (Array.isArray(pageData.contact_info)) {
          setContacts(pageData.contact_info);
        } else if (pageData.contact_info && typeof pageData.contact_info === 'object') {
          // Convert old format to new format
          setContacts([{ name: 'Primary', ...pageData.contact_info }]);
        } else {
          setContacts([]);
        }
        setContactInfo(pageData.contact_info || {});
        setIsLocked(pageData.is_locked || false);
      } catch (err) {
        toast.error('Failed to load page');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug, navigate, user]);

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error('Content cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        content,
        summary,
        edit_summary: editSummary || 'Edit',
        infobox: Object.keys(infobox).length > 0 ? infobox : null,
        categories: selectedCategories.length > 0 ? selectedCategories : null,
      };

      if (isPremium()) {
        const socialData = { ...socialLinks };
        if (customSocialLinks.length > 0) {
          socialData.custom = customSocialLinks;
        }
        updateData.social_links = Object.keys(socialData).length > 0 ? socialData : null;
        updateData.contact_info = contacts.length > 0 ? contacts : null;
        updateData.is_locked = isLocked;
      }

      await axios.put(`${API}/pages/${slug}`, updateData, {
        headers: getAuthHeader()
      });
      
      toast.success('Page saved successfully!');
      navigate(`/wiki/${slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const addInfoboxField = () => {
    if (newFieldName && newFieldName.trim()) {
      setInfobox({ ...infobox, [newFieldName.trim()]: '' });
      setNewFieldName('');
      setFieldDialogOpen(false);
      toast.success(`Added field: ${newFieldName.trim()}`);
    }
  };

  const removeInfoboxField = (key) => {
    const newInfobox = { ...infobox };
    delete newInfobox[key];
    setInfobox(newInfobox);
  };

  // Custom social link functions
  const addCustomSocialLink = () => {
    if (newSocialName.trim() && newSocialUrl.trim()) {
      setCustomSocialLinks([...customSocialLinks, { name: newSocialName.trim(), url: newSocialUrl.trim() }]);
      setNewSocialName('');
      setNewSocialUrl('');
      setNewSocialDialogOpen(false);
      toast.success(`Added ${newSocialName.trim()}`);
    }
  };

  const removeCustomSocialLink = (index) => {
    setCustomSocialLinks(customSocialLinks.filter((_, i) => i !== index));
  };

  const updateCustomSocialLink = (index, field, value) => {
    const updated = [...customSocialLinks];
    updated[index] = { ...updated[index], [field]: value };
    setCustomSocialLinks(updated);
  };

  // Contact functions
  const addContact = () => {
    setContacts([...contacts, { name: '', email: '', phone: '', address: '' }]);
  };

  const removeContact = (index) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  const updateContact = (index, field, value) => {
    const updated = [...contacts];
    updated[index] = { ...updated[index], [field]: value };
    setContacts(updated);
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-[#1A1A1A] rounded w-1/3" />
            <div className="h-96 bg-[#1A1A1A] rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="edit-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to={`/wiki/${slug}`}>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-[#1A1A1A]" data-testid="back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Article
              </Button>
            </Link>
            <h1 className="font-spectral text-2xl font-semibold text-white">
              Editing: {page?.title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/wiki/${slug}`}>
              <Button variant="outline" size="sm" className="border-[#333333] text-gray-400 hover:bg-[#1A1A1A] hover:text-white" data-testid="preview-btn">
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
            </Link>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F0F0F] font-medium"
              data-testid="save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary */}
            <div className="bg-[#1A1A1A] rounded-lg border border-[#252525] p-6">
              <Label htmlFor="summary" className="text-sm font-medium text-gray-400 mb-2 block">
                Article Summary
              </Label>
              <Textarea
                id="summary"
                placeholder="Brief description of this article..."
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                className="resize-none bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                rows={2}
                data-testid="summary-input"
              />
            </div>

            {/* Content Editor */}
            <div className="bg-[#1A1A1A] rounded-lg border border-[#252525] overflow-hidden">
              <div className="p-4 border-b border-[#252525]">
                <h2 className="font-medium text-white">Content</h2>
                <p className="text-xs text-gray-400 mt-1">Use the toolbar to format text, add links, images, and citations.</p>
              </div>
              <TipTapEditor 
                content={content} 
                onChange={setContent}
                placeholder="Start writing your article..."
              />
            </div>

            {/* Edit Summary */}
            <div className="bg-[#1A1A1A] rounded-lg border border-[#252525] p-6">
              <Label htmlFor="edit-summary" className="text-sm font-medium text-gray-400 mb-2 block">
                Edit Summary (optional)
              </Label>
              <Input
                id="edit-summary"
                placeholder="Briefly describe your changes..."
                value={editSummary}
                onChange={(e) => setEditSummary(e.target.value)}
                className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                data-testid="edit-summary-input"
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Infobox */}
            <div className="bg-[#1A1A1A] rounded-lg border border-[#252525] p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-medium text-white">Infobox</h2>
                <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-[#333333] text-gray-400 hover:bg-[#252525] hover:text-white" data-testid="add-infobox-field-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1A1A1A] border-[#252525]">
                    <DialogHeader>
                      <DialogTitle className="text-white">Add Infobox Field</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="field-name" className="text-gray-300">Field Name</Label>
                        <Input
                          id="field-name"
                          placeholder="e.g., Founded, CEO, Industry"
                          value={newFieldName}
                          onChange={(e) => setNewFieldName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addInfoboxField()}
                          className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                          data-testid="new-field-name-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline" className="border-[#333333] text-gray-300 hover:bg-[#252525]">
                          Cancel
                        </Button>
                      </DialogClose>
                      <Button onClick={addInfoboxField} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F0F0F]" data-testid="confirm-add-field-btn">
                        Add Field
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-400">Image URL</Label>
                  <Input
                    placeholder="https://..."
                    value={infobox.image || ''}
                    onChange={(e) => setInfobox({ ...infobox, image: e.target.value })}
                    className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                    data-testid="infobox-image-input"
                  />
                </div>
                {Object.entries(infobox).filter(([key]) => key !== 'image').map(([key, value]) => (
                  <div key={key} className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-400">{key}</Label>
                      <Input
                        value={value}
                        onChange={(e) => setInfobox({ ...infobox, [key]: e.target.value })}
                        className="bg-[#0F0F0F] border-[#252525] text-white"
                        data-testid={`infobox-${key}-input`}
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeInfoboxField(key)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 mt-5"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-[#1A1A1A] rounded-lg border border-[#252525] p-6">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="h-4 w-4 text-[#D4AF37]" />
                <h2 className="font-medium text-white">Categories</h2>
              </div>
              <p className="text-xs text-gray-400 mb-3">Select one or more categories for this page.</p>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORY_OPTIONS.map((cat) => {
                  const isSelected = selectedCategories.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => toggleCategory(cat.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                        isSelected 
                          ? 'bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]' 
                          : 'bg-[#0F0F0F] border-[#252525] text-gray-400 hover:border-[#333333] hover:text-gray-300'
                      }`}
                      data-testid={`category-tile-${cat.id.toLowerCase()}`}
                    >
                      <cat.icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Premium Features Alert */}
            {showPremiumAlert && !canEditPremiumFeatures && (
              <Alert className="bg-[#D4AF37]/10 border-[#D4AF37]/30">
                <Lock className="h-4 w-4 text-[#D4AF37]" />
                <AlertDescription className="text-[#D4AF37]">
                  Social links, contact info, and page locking are only available after claiming this page.{' '}
                  <Link to={`/wiki/${slug}`} className="underline font-medium">Go to page to claim</Link>
                </AlertDescription>
              </Alert>
            )}

            {/* Premium Features - Unlocked for sponsors */}
            {canEditPremiumFeatures ? (
              <>
                {/* Lock Page */}
                <div className="bg-[#1A1A1A] rounded-lg border border-[#D4AF37]/30 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white flex items-center gap-2">
                        {isLocked ? <Lock className="h-4 w-4 text-[#D4AF37]" /> : <Unlock className="h-4 w-4 text-gray-400" />}
                        Lock Page
                      </h3>
                      <p className="text-xs text-gray-400 mt-1">Prevent others from editing</p>
                    </div>
                    <Switch
                      checked={isLocked}
                      onCheckedChange={setIsLocked}
                      data-testid="lock-page-switch"
                    />
                  </div>
                </div>

                {/* Social Links */}
                <div className="bg-[#1A1A1A] rounded-lg border border-[#D4AF37]/30 p-6">
                  <h2 className="font-medium text-white mb-4 flex items-center gap-2">
                    <Globe className="h-4 w-4 text-[#D4AF37]" />
                    Official Links
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-gray-400 flex items-center gap-1">
                        <Globe className="h-3 w-3" /> Website
                      </Label>
                      <Input
                        placeholder="https://company.com"
                        value={socialLinks.website || ''}
                        onChange={(e) => setSocialLinks({ ...socialLinks, website: e.target.value })}
                        className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                        data-testid="social-website-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 flex items-center gap-1">
                        <Linkedin className="h-3 w-3" /> LinkedIn
                      </Label>
                      <Input
                        placeholder="https://linkedin.com/company/..."
                        value={socialLinks.linkedin || ''}
                        onChange={(e) => setSocialLinks({ ...socialLinks, linkedin: e.target.value })}
                        className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                        data-testid="social-linkedin-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 flex items-center gap-1">
                        <Twitter className="h-3 w-3" /> Twitter/X
                      </Label>
                      <Input
                        placeholder="https://twitter.com/..."
                        value={socialLinks.twitter || ''}
                        onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                        className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                        data-testid="social-twitter-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 flex items-center gap-1">
                        <Instagram className="h-3 w-3" /> Instagram
                      </Label>
                      <Input
                        placeholder="https://instagram.com/..."
                        value={socialLinks.instagram || ''}
                        onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                        className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                        data-testid="social-instagram-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 flex items-center gap-1">
                        <Facebook className="h-3 w-3" /> Facebook
                      </Label>
                      <Input
                        placeholder="https://facebook.com/..."
                        value={socialLinks.facebook || ''}
                        onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                        className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                        data-testid="social-facebook-input"
                      />
                    </div>

                    {/* Custom Social Links */}
                    {customSocialLinks.map((link, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-xs text-gray-400">{link.name}</Label>
                          <Input
                            placeholder="https://..."
                            value={link.url}
                            onChange={(e) => updateCustomSocialLink(index, 'url', e.target.value)}
                            className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomSocialLink(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 mb-0.5"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}

                    {/* Add Custom Social Link */}
                    <Dialog open={newSocialDialogOpen} onOpenChange={setNewSocialDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-dashed border-[#333333] text-gray-400 hover:bg-[#252525] hover:text-white mt-2"
                          data-testid="add-social-link-btn"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Other Social Network
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#1A1A1A] border-[#252525]">
                        <DialogHeader>
                          <DialogTitle className="text-white">Add Social Network</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="social-name" className="text-gray-300">Platform Name</Label>
                            <Input
                              id="social-name"
                              placeholder="e.g., TikTok, YouTube, Discord"
                              value={newSocialName}
                              onChange={(e) => setNewSocialName(e.target.value)}
                              className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="social-url" className="text-gray-300">Profile URL</Label>
                            <Input
                              id="social-url"
                              placeholder="https://..."
                              value={newSocialUrl}
                              onChange={(e) => setNewSocialUrl(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addCustomSocialLink()}
                              className="bg-[#0F0F0F] border-[#252525] text-white placeholder:text-gray-400"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline" className="border-[#333333] text-gray-300 hover:bg-[#252525]">
                              Cancel
                            </Button>
                          </DialogClose>
                          <Button onClick={addCustomSocialLink} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F0F0F]">
                            Add Link
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-[#1A1A1A] rounded-lg border border-[#D4AF37]/30 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-medium text-white flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#D4AF37]" />
                      Contact Information
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addContact}
                      className="border-[#333333] text-gray-400 hover:bg-[#252525] hover:text-white"
                      data-testid="add-contact-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {contacts.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-400 text-sm mb-3">No contacts added yet</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addContact}
                        className="border-dashed border-[#333333] text-gray-400 hover:bg-[#252525] hover:text-white"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Contact
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {contacts.map((contact, index) => (
                        <div key={index} className="bg-[#0F0F0F] rounded-lg p-4 border border-[#252525]">
                          <div className="flex items-center justify-between mb-3">
                            <Input
                              placeholder="Contact Name (e.g., Main Office, Support)"
                              value={contact.name || ''}
                              onChange={(e) => updateContact(index, 'name', e.target.value)}
                              className="bg-transparent border-none text-white font-medium placeholder:text-gray-500 p-0 h-auto focus-visible:ring-0"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeContact(index)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 -mr-2"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
                              <Input
                                type="email"
                                placeholder="email@company.com"
                                value={contact.email || ''}
                                onChange={(e) => updateContact(index, 'email', e.target.value)}
                                className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-gray-500 h-8 text-sm"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />
                              <Input
                                type="tel"
                                placeholder="+1 (555) 123-4567"
                                value={contact.phone || ''}
                                onChange={(e) => updateContact(index, 'phone', e.target.value)}
                                className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-gray-500 h-8 text-sm"
                              />
                            </div>
                            <div className="flex items-start gap-2">
                              <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0 mt-2" />
                              <Textarea
                                placeholder="123 Main St, City, State 12345"
                                value={contact.address || ''}
                                onChange={(e) => updateContact(index, 'address', e.target.value)}
                                rows={2}
                                className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-gray-500 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Social Links (Locked) */}
                <div 
                  className="bg-[#1A1A1A] rounded-lg border border-[#252525] p-6 relative opacity-60 cursor-not-allowed"
                  onClick={() => {
                    setShowPremiumAlert(true);
                    toast.info('Social links are only available after claiming this page');
                  }}
                  data-testid="social-links-section-locked"
                >
                  <div className="absolute inset-0 flex items-center justify-center bg-[#0F0F0F]/50 rounded-lg z-10">
                    <div className="flex items-center gap-2 text-[#D4AF37] bg-[#1A1A1A] px-3 py-1.5 rounded-full border border-[#D4AF37]/30">
                      <Lock className="h-3 w-3" />
                      <span className="text-xs font-medium">Claim to unlock</span>
                    </div>
                  </div>
                  <h2 className="font-medium text-white mb-4 flex items-center gap-2">
                    <Globe className="h-4 w-4 text-[#D4AF37]" />
                    Social Links
                  </h2>
                  <div className="space-y-3 pointer-events-none">
                    <div>
                      <Label className="text-xs text-gray-400">Website</Label>
                      <Input placeholder="https://company.com" disabled className="bg-[#0F0F0F] border-[#252525] text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">LinkedIn</Label>
                      <Input placeholder="https://linkedin.com/company/..." disabled className="bg-[#0F0F0F] border-[#252525] text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Twitter/X</Label>
                      <Input placeholder="https://twitter.com/..." disabled className="bg-[#0F0F0F] border-[#252525] text-white" />
                    </div>
                  </div>
                </div>

                {/* Contact Info (Locked) */}
                <div 
                  className="bg-[#1A1A1A] rounded-lg border border-[#252525] p-6 relative opacity-60 cursor-not-allowed"
                  onClick={() => {
                    setShowPremiumAlert(true);
                    toast.info('Contact info is only available after claiming this page');
                  }}
                  data-testid="contact-info-section-locked"
                >
                  <div className="absolute inset-0 flex items-center justify-center bg-[#0F0F0F]/50 rounded-lg z-10">
                    <div className="flex items-center gap-2 text-[#D4AF37] bg-[#1A1A1A] px-3 py-1.5 rounded-full border border-[#D4AF37]/30">
                      <Lock className="h-3 w-3" />
                      <span className="text-xs font-medium">Claim to unlock</span>
                    </div>
                  </div>
                  <h2 className="font-medium text-white mb-4 flex items-center gap-2">
                    <Mail className="h-4 w-4 text-[#D4AF37]" />
                    Contact Information
                  </h2>
                  <div className="space-y-3 pointer-events-none">
                    <div>
                      <Label className="text-xs text-gray-400">Email</Label>
                      <Input type="email" placeholder="contact@company.com" disabled className="bg-[#0F0F0F] border-[#252525] text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Phone</Label>
                      <Input type="tel" placeholder="+1 (555) 123-4567" disabled className="bg-[#0F0F0F] border-[#252525] text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Address</Label>
                      <Textarea placeholder="123 Main St, City, State 12345" disabled rows={2} className="bg-[#0F0F0F] border-[#252525] text-white" />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
